import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import type { SelectOption } from '@fastnorth/react-cerberus/Form';
import { FormGroup, FormLabel, FormMultiLine, FormSelect } from '@fastnorth/react-cerberus/Form';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import type { ReportInput } from './queries/sendReport';
import deriveRecaptchaKey from './utils/deriveRecaptchaKey';

type ReportFormType = {
  setInput: Dispatch<ReportInput>;
  setRecaptchaToken: Dispatch<SetStateAction<string | undefined>>;
  recaptchaError: boolean;
  setRecaptchaError: Dispatch<SetStateAction<boolean>>;
};

const ReportForm = forwardRef<HTMLFormElement, ReportFormType>(
  ({ setInput, setRecaptchaToken, recaptchaError, setRecaptchaError }: ReportFormType, ref): ReactElement => {
    const { t } = useTranslation();
    const [reason, setReason] = useState<string | undefined>('');
    const [details, setDetails] = useState<string | undefined>();
    const recaptchaSiteKey = deriveRecaptchaKey();

    const reasons: SelectOption[] = [
      { value: 'nudity', label: t('photos_ui.report.reason.nudity') },
      { value: 'violence', label: t('photos_ui.report.reason.violence') },
      { value: 'visually_disturbing', label: t('photos_ui.report.reason.visually_disturbing') },
      { value: 'other', label: t('photos_ui.report.reason.other') },
    ];

    const onRecaptchaChange = (token?: string): void => {
      setRecaptchaToken(token);
      setRecaptchaError(!token);
    };

    const onExpired = (): void => {
      setRecaptchaToken(undefined);
      setRecaptchaError(true);
    };

    useEffect(() => {
      setInput({
        reason,
        details,
      });
    }, [reason, details, setInput]);

    return (
      <form ref={ref} role="form" style={{ pointerEvents: 'all' }}>
        <FormGroup>
          <FormLabel required={true} htmlFor={'report_reason'}>
            {t('photos_ui.report.reason_label')}
          </FormLabel>
          <FormSelect
            id={'report_reason'}
            required={true}
            placeholder={t('photos_ui.report.reason.placeholder')}
            options={reasons}
            value={reason}
            onChange={(val) => setReason(val)}
          />
        </FormGroup>
        {reason === 'other' && (
          <FormGroup>
            <FormLabel required={reason === 'other'} htmlFor={'report_description'}>
              {t('photos_ui.report.description_label')}
            </FormLabel>
            <FormMultiLine
              required={reason === 'other'}
              id={'report_description'}
              value={details}
              onChange={(val) => setDetails(val)}>
              {details}
            </FormMultiLine>
          </FormGroup>
        )}
        <FormGroup>
          <ReCAPTCHA
            className={'mb-1'}
            sitekey={recaptchaSiteKey}
            onChange={(token: string | null) => onRecaptchaChange(token || undefined)}
            onExpired={onExpired}
          />
          {recaptchaError && <p className={'text-danger small'}>{t('photos_ui.report.error.recaptcha')}</p>}
        </FormGroup>
      </form>
    );
  },
);
ReportForm.displayName = 'ReportForm'; // make eslint happy

export default ReportForm;
